import { useEffect, useState } from "react";
import { moment } from "../../../../vendor";
import { Biscuit } from "../../../utils";
import Inboomai18n from "../../../utils/Inboomai18n";
import { getApiHost } from "../../../Sync";
import t from "../../helpers/t";
import UiModal from "../ui/UiModal/UiModal";

type CheckTimeResponse = {
  success: boolean;
  time: string;
  timeDiffInMinutes: number;
};

/**
 * Checks the device time against the server time via POST /users/checkTime
 * (once on mount) and shows a modal if the deviation is too large.
 * Renders nothing itself as long as no notice needs to be shown.
 *
 * @example
 * <CheckTime />
 */
export default function CheckTime() {
  const [result, setResult] = useState<CheckTimeResponse | null>(null);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const locale = Inboomai18n.getActiveLanguageCode() || "de_DE";
  const momentLocale = locale.split("_")[0] || "de";

  const checkTime = async (isOnClick: boolean = false): Promise<void> => {
    const time = new Date().toISOString();
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    setIsLoading(true);

    const response = await fetch(`${getApiHost()}/users/checkTime`, {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        Authorization: `Bearer ${Biscuit.get("token")}`,
      },
      body: JSON.stringify({ time, timeZone }),
    });

    if (!response.ok) {
      setTimeout(() => setIsLoading(false), 1000);
      throw new Error(`checkTime failed: ${response.status}`);
    }

    const data = (await response.json()) as CheckTimeResponse;

    if (!data.success) {
      setResult(data);
      setShowModal(true);
    }

    if (data.success) {
      setShowModal(false)
      if (isOnClick) window.location.reload();
    }
    
    setTimeout(() => setIsLoading(false), 1000);
  };

  useEffect(() => {
    checkTime().catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!showModal || !result) return null;

  return (
    <UiModal
      open={showModal}
      showCloseButton={false}
      onClose={() => setShowModal(false)}
      title={t("wrong_device_time_title")}
      closeOnOverlayClick={false}
      closeOnEscape={false}
      buttons={[
        {
          text: t("wrong_device_time_check_again"),
          onClick: () => {
            checkTime(true).catch(() => {});
          },
          loading: isLoading,
        },
      ]}
    >
      <div style={{ textAlign: "center" }}>
        <p>{t("wrong_device_time_msg")}</p>
        <p>
          {t("wrong_device_time_deviation")}: {result.timeDiffInMinutes} min
          <br />
          {t("wrong_device_time_current")}:{" "}
          {moment(result.time).locale(momentLocale).format("HH:mm, DD.MM.YY")}
          <br />
          {t("wrong_device_time_device")}: {moment().locale(momentLocale).format("HH:mm, DD.MM.YY")}
        </p>
      </div>
    </UiModal>
  );
}
